import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError, money, fmtShort } from "../lib/api";
import { toast } from "sonner";
import { Funnel, MagnifyingGlass, ArrowClockwise, ListChecks, X, CodeSimple } from "@phosphor-icons/react";

const EVENT_TYPE_META = {
  "sale.created":       { label: "Sale",         color: "#0F9D58", soft: "#ECFDF5" },
  "sale.payment_added": { label: "Top-up",       color: "#1E5FEB", soft: "#DBEAFE" },
  "sale.voided":        { label: "Void",         color: "#DC2626", soft: "#FEE2E2" },
  "sale.refunded":      { label: "Refund",       color: "#DC2626", soft: "#FEE2E2" },
  "sale.deleted":       { label: "Sale deleted", color: "#DC2626", soft: "#FEE2E2" },
  "sale.edited":        { label: "Sale edit",    color: "#C98B24", soft: "#FEF3C7" },
  "ticket.deleted":     { label: "Ticket del.",  color: "#DC2626", soft: "#FEE2E2" },
  "shift.opened":       { label: "Shift open",   color: "#1E5FEB", soft: "#DBEAFE" },
  "shift.closed":       { label: "Shift close",  color: "#121216", soft: "#F3F4F6" },
  "cash_out.created":   { label: "Cash out",     color: "#92400E", soft: "#FEF3C7" },
  "cash_in.created":    { label: "Cash in",      color: "#065F46", soft: "#D1FAE5" },
};

function eventMeta(t) {
  return EVENT_TYPE_META[t] || { label: t, color: "#4B5563", soft: "#F3F4F6" };
}

// — Field name → human label
function prettyKey(key) {
  // Friendly overrides for keys we know look bad in raw form
  const map = {
    counted_cash: "Counted cash",
    expected_cash: "Expected cash",
    opening_float: "Opening float",
    cash_out_total: "Total cash out",
    sales_total: "Sales total",
    sale_count: "Sales",
    refund_total: "Refunds",
    discount: "Discount",
    payment_method: "Payment method",
    payments: "Payments",
    items: "Items",
    sale_number: "Invoice #",
    ticket_number: "Ticket #",
    customer_name: "Customer",
    diff_keys: "Fields changed",
    totals_summary: "Totals",
    method: "Method",
    line_total: "Line total",
    unit_price: "Unit",
    amount: "Amount",
    quantity: "Qty",
    reason: "Reason",
    category: "Category",
    note: "Note",
    voided: "Voided?",
    fully_refunded: "Fully refunded?",
    opened_at: "Opened",
    closed_at: "Closed",
    device_make: "Device make",
    device_model: "Device model",
    estimated_cost: "Estimated cost",
    status: "Status",
  };
  if (map[key]) return map[key];
  return key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

const CURRENCY_KEYS = new Set([
  "counted_cash", "expected_cash", "opening_float", "cash_out_total",
  "sales_total", "refund_total", "discount", "tax", "subtotal", "total",
  "amount", "line_total", "unit_price", "estimated_cost", "final_cost",
  "balance_due", "tendered", "amount_tendered", "change_due", "variance",
  "voided_total",
]);

const COUNT_KEYS = new Set(["sale_count", "refund_count", "void_count", "quantity", "qty", "stock"]);

function isIsoDate(v) {
  return typeof v === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v);
}

function fmtDate(iso) {
  // Format an audit-log timestamp in the store's local POS timezone
  // (Australia/Brisbane by default). Previously we just sliced the raw
  // UTC ISO string which made every entry look 10 hours behind for AU
  // staff and caused split-tender summaries to appear on the wrong day.
  return fmtShort(iso);
}

// — Format a single value (currency / date / boolean / etc) given its key
function fmtValue(key, value) {
  if (value === null || value === undefined || value === "") return "—";
  if (CURRENCY_KEYS.has(key) && typeof value === "number") return `$${money(value)}`;
  if (COUNT_KEYS.has(key) && typeof value === "number") return value.toLocaleString();
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (isIsoDate(value)) return fmtDate(value);
  return String(value);
}

// — Render a flat key/value pair (right-aligned numbers / currency)
function KvRow({ k, v }) {
  const numeric =
    typeof v === "number" && (CURRENCY_KEYS.has(k) || COUNT_KEYS.has(k));
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-[#F3F4F6] last:border-0">
      <div className="text-[12px] text-[#6B7280]">{prettyKey(k)}</div>
      <div className={`text-[13px] font-semibold tabular-nums ${numeric ? "font-mono" : ""}`}>
        {fmtValue(k, v)}
      </div>
    </div>
  );
}

// — Render a Sale-items list (most common nested structure on `after.items`)
function ItemsTable({ items }) {
  if (!Array.isArray(items) || items.length === 0) {
    return <div className="text-[12px] text-[#9CA3AF]">No items</div>;
  }
  return (
    <div className="rounded-lg border border-[#E5E7EB] overflow-hidden bg-white">
      <div className="grid grid-cols-12 gap-2 px-3 py-1.5 bg-[#F8FAFC] text-[10px] uppercase tracking-[0.12em] text-[#9CA3AF] font-medium">
        <div className="col-span-7">Item</div>
        <div className="col-span-2 text-right">Qty</div>
        <div className="col-span-3 text-right">Total</div>
      </div>
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 px-3 py-1.5 text-[12px] border-t border-[#F3F4F6]">
          <div className="col-span-7 truncate">{it.name || it.sku || "—"}</div>
          <div className="col-span-2 text-right tabular-nums">{it.quantity ?? it.qty ?? "—"}</div>
          <div className="col-span-3 text-right tabular-nums font-mono">${money(it.line_total ?? it.unit_price ?? 0)}</div>
        </div>
      ))}
    </div>
  );
}

// — Friendly auto-renderer for a snapshot dict (one or two levels deep).
// Designed NOT to be recursive — we walk one level of nesting inline so the
// visual-edits babel plugin doesn't get tripped up by a self-referential JSX call.
function FriendlyDict({ data }) {
  if (data == null) {
    return <div className="text-[12px] text-[#9CA3AF] italic px-3 py-3">— Nothing recorded —</div>;
  }
  if (typeof data !== "object" || Array.isArray(data)) {
    return <pre className="text-[11px] font-mono p-3">{JSON.stringify(data, null, 2)}</pre>;
  }
  const entries = Object.entries(data);
  // Items array always rendered as table
  const itemsEntry = entries.find(([k]) => k === "items");
  const totalsEntry = entries.find(([k]) => k === "totals_summary");
  const otherEntries = entries.filter(([k]) => k !== "items" && k !== "totals_summary" && k !== "diff_keys");
  const diffKeys = (entries.find(([k]) => k === "diff_keys") || [])[1];
  return (
    <div className="space-y-3">
      {otherEntries.length > 0 && (
        <div className="rounded-lg border border-[#E5E7EB] bg-white px-3 py-1">
          {otherEntries.map(([k, v]) => {
            // Nested object (other than items / totals_summary) → render inline
            // as a labelled key→value mini-block (one level deep, no recursion).
            if (v && typeof v === "object" && !Array.isArray(v)) {
              const subEntries = Object.entries(v);
              return (
                <div key={k} className="py-1.5 border-b border-[#F3F4F6] last:border-0">
                  <div className="text-[11px] uppercase tracking-[0.12em] text-[#9CA3AF] font-medium mb-1">{prettyKey(k)}</div>
                  <div className="pl-2 rounded-md bg-[#F8FAFC] px-2 py-1">
                    {subEntries.length === 0 ? (
                      <div className="text-[12px] text-[#9CA3AF]">—</div>
                    ) : (
                      subEntries.map(([sk, sv]) => <KvRow key={sk} k={sk} v={sv} />)
                    )}
                  </div>
                </div>
              );
            }
            // Array of payments (split tender) → small list
            if (Array.isArray(v) && k === "payments") {
              return (
                <div key={k} className="py-1.5 border-b border-[#F3F4F6] last:border-0">
                  <div className="text-[11px] uppercase tracking-[0.12em] text-[#9CA3AF] font-medium mb-1">Payments</div>
                  <div className="space-y-1 pl-2">
                    {v.map((p, i) => (
                      <div key={i} className="flex items-center justify-between text-[12px] bg-[#F8FAFC] rounded px-2 py-1">
                        <span className="capitalize">{(p.method || "").replace("_manual", "")}</span>
                        <span className="font-mono font-semibold">${money(p.amount || 0)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            return <KvRow key={k} k={k} v={v} />;
          })}
        </div>
      )}
      {itemsEntry && (
        <div>
          <div className="text-[11px] uppercase tracking-[0.12em] text-[#9CA3AF] font-medium mb-1">Items</div>
          <ItemsTable items={itemsEntry[1]} />
        </div>
      )}
      {totalsEntry && (
        <div>
          <div className="text-[11px] uppercase tracking-[0.12em] text-[#9CA3AF] font-medium mb-1">Totals</div>
          <div className="rounded-lg border border-[#E5E7EB] bg-white px-3 py-1">
            {Object.entries(totalsEntry[1] || {}).map(([k, v]) => (
              <KvRow key={k} k={k} v={v} />
            ))}
          </div>
        </div>
      )}
      {Array.isArray(diffKeys) && diffKeys.length > 0 && (
        <div className="text-[11px] text-[#9CA3AF]">
          Fields changed: {diffKeys.map((k) => prettyKey(k)).join(", ")}
        </div>
      )}
    </div>
  );
}

// — Single-side panel
function SidePanel({ side, data, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.16em] font-semibold mb-2" style={{ color: accent }}>
        {side}
      </div>
      <FriendlyDict data={data} />
    </div>
  );
}

export default function AuditLogTab() {
  const [items, setItems] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [activeType, setActiveType] = useState("");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  // Debounced search.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async (newSkip = 0, append = false) => {
    setLoading(true);
    try {
      const params = { limit: 50, skip: newSkip };
      if (activeType) params.event_type = activeType;
      if (debouncedQ) params.q = debouncedQ;
      const r = await api.get("/transaction-logs", { params });
      const newItems = r.data?.items || [];
      setItems(append ? [...items, ...newItems] : newItems);
      setTotal(r.data?.total || 0);
      setHasMore(r.data?.has_more || false);
      setSkip(newSkip + newItems.length);
      if (!append) setEventTypes(r.data?.event_types || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeType, debouncedQ]);

  useEffect(() => { load(0, false); /* eslint-disable-next-line */ }, [activeType, debouncedQ]);

  return (
    <div className="px-8 py-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="font-display font-semibold text-[20px]">Audit log</div>
          <div className="text-[12px] text-[#9CA3AF]">Every sale, refund, void, edit, delete, cash-out, and shift event for this store.</div>
        </div>
        <button
          onClick={() => load(0, false)}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#E5E7EB] text-[12px] font-semibold hover:border-[#1E5FEB] hover:text-[#1E5FEB]"
          data-testid="audit-refresh"
        >
          <ArrowClockwise size={12} weight="bold" />
          Refresh
        </button>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          onClick={() => setActiveType("")}
          data-testid="audit-filter-all"
          className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-[12px] font-semibold transition-all ${
            activeType === ""
              ? "bg-[#121216] border-[#121216] text-white"
              : "bg-white border-[#E5E7EB] text-[#4B5563] hover:border-[#121216]"
          }`}
        >
          <ListChecks size={12} weight="bold" />
          All events
        </button>
        {eventTypes.map((t) => {
          const meta = eventMeta(t);
          const active = activeType === t;
          return (
            <button
              key={t}
              onClick={() => setActiveType(active ? "" : t)}
              data-testid={`audit-filter-${t.replace(/\./g, "-")}`}
              className={`inline-flex items-center gap-1.5 h-8 pl-2.5 pr-3 rounded-full border text-[12px] font-semibold transition-all ${
                active
                  ? "border-transparent text-white"
                  : "bg-white border-[#E5E7EB] text-[#4B5563] hover:border-[#121216] hover:text-[#121216]"
              }`}
              style={active ? { background: meta.color } : undefined}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: active ? "#ffffff" : meta.color }}
              />
              {meta.label}
            </button>
          );
        })}
        <div className="ml-auto relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search summary / actor / entity…"
            className="h-9 pl-9 pr-8 rounded-full border border-[#E5E7EB] text-[13px] w-72 focus:border-[#1E5FEB] focus:outline-none"
            data-testid="audit-search"
          />
          {q && (
            <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#121216]">
              <X size={12} weight="bold" />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-white border border-[#E5E7EB] overflow-hidden" data-testid="audit-log-table">
        <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-[#F8FAFC] border-b border-[#F3F4F6] text-[10px] uppercase tracking-[0.12em] text-[#9CA3AF] font-medium">
          <div className="col-span-2">Time</div>
          <div className="col-span-2">Event</div>
          <div className="col-span-5">Summary</div>
          <div className="col-span-2">Actor</div>
          <div className="col-span-1 text-right">Amount</div>
        </div>
        {items.length === 0 && !loading ? (
          <div className="p-8 text-center text-[13px] text-[#9CA3AF]">No audit entries match.</div>
        ) : (
          <div className="divide-y divide-[#F3F4F6]">
            {items.map((it) => {
              const meta = eventMeta(it.event_type);
              const ts = fmtShort(it.created_at);
              const isExpanded = expanded === it.id;
              return (
                <div key={it.id}>
                  <button
                    onClick={() => setExpanded(isExpanded ? null : it.id)}
                    className="w-full grid grid-cols-12 gap-3 px-4 py-3 text-[13px] text-left hover:bg-[#F8FAFC]"
                    data-testid={`audit-row-${it.id}`}
                  >
                    <div className="col-span-2 font-mono text-[11px] text-[#4B5563]">{ts}</div>
                    <div className="col-span-2">
                      <span
                        className="inline-block px-2 py-0.5 rounded-md text-[11px] font-semibold"
                        style={{ background: meta.soft, color: meta.color }}
                      >
                        {meta.label}
                      </span>
                    </div>
                    <div className="col-span-5 truncate" title={it.summary}>
                      <span className="font-semibold text-[#121216]">{it.entity_label || ""}</span>
                      {it.entity_label && " · "}
                      <span className="text-[#4B5563]">{it.summary}</span>
                    </div>
                    <div className="col-span-2 text-[12px] truncate text-[#4B5563]">{it.actor_name}</div>
                    <div className="col-span-1 text-right font-mono tabular-nums font-semibold">
                      {it.amount ? `$${money(Math.abs(it.amount))}` : "—"}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="px-6 pb-5 pt-2 bg-[#F8FAFC]">
                      <ExpandedRow log={it} meta={meta} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div className="px-4 py-3 flex items-center justify-between text-[12px] text-[#9CA3AF] bg-[#F8FAFC] border-t border-[#F3F4F6]">
          <div>
            Showing {items.length} of {total.toLocaleString()}
          </div>
          {hasMore && (
            <button
              onClick={() => load(skip, true)}
              className="font-semibold text-[#1E5FEB] hover:underline"
              data-testid="audit-load-more"
              disabled={loading}
            >
              {loading ? "Loading…" : `Load 50 more →`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


// — Friendly expanded row content. Renders a side-by-side "before / after"
// view using the FriendlyDict renderer so staff don't have to read JSON.
// Power users can tap the "Show raw JSON" toggle to see the underlying data.
function ExpandedRow({ log, meta }) {
  const [showRaw, setShowRaw] = useState(false);
  // Shift-close events get a dedicated, human-readable Z-report block
  // instead of the generic before/after JSON view. Admins want to
  // read the till reconciliation quickly, not decode a nested dict.
  if (log.event_type === "shift.closed" && log.after) {
    return <ShiftClosedZReport log={log} onShowRaw={() => setShowRaw(!showRaw)} showRaw={showRaw} />;
  }
  const hasBefore = log.before && (typeof log.before !== "object" || Object.keys(log.before).length > 0);
  const hasAfter = log.after && (typeof log.after !== "object" || Object.keys(log.after).length > 0);
  const showSplit = hasBefore && hasAfter;
  return (
    <div className="text-[12px]">
      {/* Headline summary */}
      <div className="rounded-xl border-l-4 px-4 py-3 bg-white mb-3" style={{ borderColor: meta.color }}>
        <div className="text-[13px] font-semibold text-[#121216]">{log.summary || meta.label}</div>
        {log.entity_label && (
          <div className="text-[11px] text-[#9CA3AF] mt-0.5">
            <span className="font-mono">{log.entity_label}</span>
            {log.entity_type && <> · {log.entity_type.replace(/_/g, " ")}</>}
          </div>
        )}
      </div>

      {!showRaw ? (
        <div className={`grid gap-4 ${showSplit ? "md:grid-cols-2" : "grid-cols-1"}`}>
          {hasBefore && (
            <SidePanel side="Before" data={log.before} accent="#9CA3AF" />
          )}
          {hasAfter && (
            <SidePanel side={hasBefore ? "After" : "Details"} data={log.after} accent={meta.color} />
          )}
          {!hasBefore && !hasAfter && (
            <div className="text-[12px] text-[#9CA3AF] italic">No additional details recorded.</div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 font-mono">
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#9CA3AF] mb-1">Before (raw)</div>
            <pre className="whitespace-pre-wrap break-words bg-white border border-[#E5E7EB] rounded-lg p-3 text-[11px]">{JSON.stringify(log.before, null, 2) || "null"}</pre>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#9CA3AF] mb-1">After (raw)</div>
            <pre className="whitespace-pre-wrap break-words bg-white border border-[#E5E7EB] rounded-lg p-3 text-[11px]">{JSON.stringify(log.after, null, 2) || "null"}</pre>
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between text-[11px] text-[#9CA3AF]">
        <div className="flex items-center gap-3">
          {log.ip && <span>IP: <span className="font-mono">{log.ip}</span></span>}
          <span>Logged: <span className="font-mono">{fmtShort(log.created_at)}</span></span>
        </div>
        <button
          onClick={() => setShowRaw((r) => !r)}
          className="inline-flex items-center gap-1 hover:text-[#121216]"
          data-testid="audit-toggle-raw"
        >
          <CodeSimple size={12} weight="bold" />
          {showRaw ? "Friendly view" : "Show raw JSON"}
        </button>
      </div>
    </div>
  );
}


/**
 * Z-report card — shift.closed audit event.
 *
 * Renders the till reconciliation as a clean, human-readable Z-report
 * (opening float, sales by method, cash injections, refunds by method,
 * cash-outs listed, expected/counted/variance) with a "Download PDF"
 * button that hits /shifts/{id}/z-report.pdf. Admins can inspect any
 * closed shift from the audit log without opening the Till page.
 */
function ShiftClosedZReport({ log, showRaw, onShowRaw }) {
  const a = log.after || {};
  const by = a.by_method || {};
  const rbm = a.refund_by_method || {};
  const cashIns = a.cash_ins || [];
  const cashOuts = a.cash_outs || [];
  const fmt = (n) => `$${money(Number(n || 0))}`;
  const variance = Number(a.variance || 0);
  const varianceColor = Math.abs(variance) < 0.01 ? "text-emerald-600"
    : variance > 0 ? "text-emerald-600" : "text-red-600";
  const downloadPdf = async () => {
    try {
      const r = await api.get(`/shifts/${log.entity_id}/z-report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't fetch Z-report");
    }
  };

  if (showRaw) {
    return (
      <div>
        <pre className="text-[11px] font-mono bg-white border border-[#E5E7EB] rounded-lg p-3 overflow-x-auto max-h-96">
          {JSON.stringify(a, null, 2)}
        </pre>
        <button onClick={onShowRaw} className="mt-2 text-[11px] text-[#1E5FEB] hover:underline font-semibold" data-testid="audit-toggle-raw">
          Back to Z-report view
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden" data-testid="audit-z-report">
      <div className="px-4 py-3 border-b border-[#F3F4F6] flex items-center justify-between bg-[#F8FAFC]">
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#9CA3AF] font-bold">Z-REPORT</div>
          <div className="text-[13px] font-semibold text-[#121216] mt-0.5">
            {a.opened_by_name || "—"}
            {a.closed_by_name && a.closed_by_name !== a.opened_by_name ? ` → ${a.closed_by_name}` : ""}
            <span className="text-[#9CA3AF] font-normal font-mono ml-2">
              {fmtShort(a.opened_at)} → {fmtShort(a.closed_at)}
            </span>
          </div>
        </div>
        <button
          onClick={downloadPdf}
          className="h-9 px-3 rounded-lg border border-[#E5E7EB] hover:border-[#1E5FEB] hover:text-[#1E5FEB] text-[11px] font-bold tracking-wider uppercase"
          data-testid="audit-z-report-download"
        >
          Download PDF
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-[#F3F4F6]">
        <div className="p-4">
          <SectionLabel>Drawer</SectionLabel>
          <ZRow k="Opening float" v={fmt(a.opening_float)} />
          <ZRow k="Cash sales (+)" v={fmt(by.cash)} tone="emerald" />
          {Number(a.cash_in_total || 0) > 0 && (
            <ZRow k="Cash injected (+)" v={fmt(a.cash_in_total)} tone="emerald" />
          )}
          {Number(rbm.cash || 0) > 0 && (
            <ZRow k="Cash refunds (−)" v={fmt(rbm.cash)} tone="red" />
          )}
          <ZRow k="Cash out (−)" v={fmt(a.cash_out_total)} tone="red" />
          <div className="mt-3 pt-3 border-t border-[#F3F4F6] space-y-1">
            <ZRow k="Expected cash" v={fmt(a.expected_cash)} strong />
            <ZRow k="Counted cash" v={fmt(a.counted_cash)} strong />
            <div className={`flex items-baseline justify-between text-[13px] font-mono font-bold ${varianceColor}`}>
              <span>Variance</span>
              <span data-testid="audit-z-variance">
                {variance >= 0 ? "+" : ""}{fmt(variance)}
              </span>
            </div>
          </div>
        </div>

        <div className="p-4">
          <SectionLabel>Payments received</SectionLabel>
          <ZRow k="Cash" v={fmt(by.cash)} />
          <ZRow k="Card" v={fmt(by.card_manual)} />
          {Number(by.tyro || 0) > 0 && <ZRow k="EFTPOS · Tyro" v={fmt(by.tyro)} />}
          {Number(by.other || 0) > 0 && <ZRow k="Other" v={fmt(by.other)} />}
          <div className="mt-3 pt-3 border-t border-[#F3F4F6]">
            <ZRow k="Total received" v={fmt(a.payments_received_total)} strong />
          </div>
          {Number(a.refund_total || 0) > 0 && (
            <>
              <SectionLabel className="mt-4">Refunds</SectionLabel>
              {Number(rbm.cash || 0) > 0 && <ZRow k="Cash" v={fmt(rbm.cash)} tone="red" />}
              {Number(rbm.card_manual || 0) > 0 && <ZRow k="Card" v={fmt(rbm.card_manual)} tone="red" />}
              {Number(rbm.tyro || 0) > 0 && <ZRow k="Tyro" v={fmt(rbm.tyro)} tone="red" />}
              {Number(rbm.other || 0) > 0 && <ZRow k="Other" v={fmt(rbm.other)} tone="red" />}
              <ZRow k="Refund count" v={String(a.refund_count || 0)} muted />
            </>
          )}
        </div>

        <div className="p-4 max-h-[300px] overflow-y-auto">
          {cashIns.length > 0 && (
            <>
              <SectionLabel>Cash in log · {cashIns.length}</SectionLabel>
              {cashIns.map((c) => (
                <div key={c.id} className="flex items-center gap-2 py-1 text-[11px]" data-testid={`audit-z-cashin-${c.id}`}>
                  <div className="text-[#9CA3AF] font-mono w-24 shrink-0">{fmtShort(c.created_at)}</div>
                  <div className="flex-1 truncate">
                    <div className="font-semibold text-[#121216] truncate">{c.category}</div>
                    {c.reason && <div className="text-[10px] text-[#6B7280] truncate">{c.reason}</div>}
                  </div>
                  <div className="font-mono font-semibold text-emerald-600 tabular-nums">+{fmt(c.amount)}</div>
                </div>
              ))}
            </>
          )}
          {cashOuts.length > 0 && (
            <>
              <SectionLabel className={cashIns.length > 0 ? "mt-4" : ""}>
                Cash out log · {cashOuts.length}
              </SectionLabel>
              {cashOuts.map((c) => (
                <div key={c.id} className="flex items-center gap-2 py-1 text-[11px]" data-testid={`audit-z-cashout-${c.id}`}>
                  <div className="text-[#9CA3AF] font-mono w-24 shrink-0">{fmtShort(c.created_at)}</div>
                  <div className="flex-1 truncate">
                    <div className="font-semibold text-[#121216] truncate">{c.category}</div>
                    {c.reason && <div className="text-[10px] text-[#6B7280] truncate">{c.reason}</div>}
                  </div>
                  <div className="font-mono font-semibold text-red-600 tabular-nums">−{fmt(c.amount)}</div>
                </div>
              ))}
            </>
          )}
          {cashIns.length === 0 && cashOuts.length === 0 && (
            <div className="text-[11px] text-[#9CA3AF] italic">
              No cash movements outside sales during this shift.
            </div>
          )}
        </div>
      </div>

      {a.close_notes && (
        <div className="px-4 py-3 border-t border-[#F3F4F6] bg-[#FEF9C3]/40 text-[11px]">
          <span className="font-bold text-[#78350F] mr-2">Close notes:</span>
          <span className="text-[#4B5563]">{a.close_notes}</span>
        </div>
      )}

      <div className="px-4 py-2 border-t border-[#F3F4F6] flex items-center justify-end">
        <button
          onClick={onShowRaw}
          className="text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF] hover:text-[#121216]"
          data-testid="audit-toggle-raw"
        >
          Show raw JSON
        </button>
      </div>
    </div>
  );
}

function SectionLabel({ children, className = "" }) {
  return (
    <div className={`text-[10px] uppercase tracking-[0.16em] text-[#9CA3AF] font-bold mb-2 ${className}`}>
      {children}
    </div>
  );
}

function ZRow({ k, v, tone, strong, muted }) {
  const color = tone === "emerald" ? "text-emerald-700"
    : tone === "red" ? "text-red-700"
    : muted ? "text-[#9CA3AF]"
    : "text-[#121216]";
  return (
    <div className={`flex items-baseline justify-between text-[12px] ${strong ? "font-mono font-bold" : "font-mono"} py-0.5`}>
      <span className={muted ? "text-[#9CA3AF]" : "text-[#4B5563]"}>{k}</span>
      <span className={`${color} tabular-nums`}>{v}</span>
    </div>
  );
}

